import * as THREE from 'three';

import type { TerrainGpuDrawCommand } from './TerrainSurfaceManager';
import type { TerrainGpuPresentationMode } from './TerrainSurfaceManager';

interface TerrainGpuEntry {
  texture: THREE.CanvasTexture;
  material: THREE.MeshBasicMaterial;
  mesh: THREE.Mesh;
  sourceCanvas: HTMLCanvasElement;
}

export class TerrainGpuLayer {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(0, 1, 1, 0, 0.1, 10);
  private readonly quadGeometry = new THREE.PlaneGeometry(1, 1);
  private readonly entries = new Map<string, TerrainGpuEntry>();
  private viewportWidth = 1;
  private viewportHeight = 1;
  private seeded = false;

  constructor() {
    this.renderer = new THREE.WebGLRenderer({ antialias: false, alpha: true });
    this.renderer.domElement.style.position = 'absolute';
    this.renderer.domElement.style.inset = '0';
    this.renderer.domElement.style.pointerEvents = 'none';
    this.renderer.domElement.style.zIndex = '0';
    this.renderer.setPixelRatio(window.devicePixelRatio || 1);
    this.renderer.setClearColor(0x000000, 0);
    this.camera.position.z = 1;
  }

  public destroy(): void {
    this.clear();
    this.quadGeometry.dispose();
    this.renderer.dispose();
  }

  public getElement(): HTMLCanvasElement {
    return this.renderer.domElement;
  }

  public syncSize(width: number, height: number): void {
    this.viewportWidth = Math.max(1, Math.round(width));
    this.viewportHeight = Math.max(1, Math.round(height));
    this.renderer.setPixelRatio(window.devicePixelRatio || 1);
    this.renderer.setSize(this.viewportWidth, this.viewportHeight, false);
    this.renderer.domElement.style.width = `${this.viewportWidth}px`;
    this.renderer.domElement.style.height = `${this.viewportHeight}px`;
    this.camera.left = 0;
    this.camera.right = this.viewportWidth;
    this.camera.top = this.viewportHeight;
    this.camera.bottom = 0;
    this.camera.updateProjectionMatrix();
  }

  public clear(): void {
    for (const entry of this.entries.values()) {
      entry.texture.dispose();
      entry.material.dispose();
      this.scene.remove(entry.mesh);
    }
    this.entries.clear();
    this.renderer.clear();
    this.seeded = false;
  }

  public isSeeded(): boolean {
    return this.seeded;
  }

  public render(commands: readonly TerrainGpuDrawCommand[], mode: TerrainGpuPresentationMode = 'replace'): void {
    if (mode === 'replace') {
      const liveKeys = new Set(commands.map((command) => command.key));
      for (const entry of this.entries.values()) {
        entry.mesh.visible = false;
      }
      for (const [key, entry] of this.entries.entries()) {
        if (liveKeys.has(key)) {
          continue;
        }
        entry.texture.dispose();
        entry.material.dispose();
        this.scene.remove(entry.mesh);
        this.entries.delete(key);
      }
    }

    for (const command of commands) {
      const entry = this.getOrCreateEntry(command.key, command.texture);
      if (entry.sourceCanvas !== command.texture) {
        entry.texture.dispose();
        entry.material.dispose();
        this.scene.remove(entry.mesh);
        this.entries.delete(command.key);
        const replacement = this.getOrCreateEntry(command.key, command.texture);
        this.applyCommand(replacement, command);
        continue;
      }
      this.applyCommand(entry, command);
    }

    this.renderer.render(this.scene, this.camera);
    if (mode === 'replace') {
      this.seeded = true;
    }
  }

  private getOrCreateEntry(key: string, sourceCanvas: HTMLCanvasElement): TerrainGpuEntry {
    const existing = this.entries.get(key);
    if (existing && existing.sourceCanvas === sourceCanvas) {
      return existing;
    }

    const texture = new THREE.CanvasTexture(sourceCanvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.generateMipmaps = false;

    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(this.quadGeometry, material);
    this.scene.add(mesh);

    const entry: TerrainGpuEntry = {
      texture,
      material,
      mesh,
      sourceCanvas,
    };
    this.entries.set(key, entry);
    return entry;
  }

  private applyCommand(entry: TerrainGpuEntry, command: TerrainGpuDrawCommand): void {
    const texture = entry.texture;
    texture.repeat.set(
      command.sourceWidth / Math.max(1, command.texture.width),
      command.sourceHeight / Math.max(1, command.texture.height),
    );
    texture.offset.set(
      command.sourceX / Math.max(1, command.texture.width),
      1 - ((command.sourceY + command.sourceHeight) / Math.max(1, command.texture.height)),
    );
    if (command.needsUpload) {
      texture.needsUpdate = true;
    }

    entry.mesh.position.set(
      command.destX + (command.destWidth / 2),
      this.viewportHeight - (command.destY + (command.destHeight / 2)),
      0,
    );
    entry.mesh.scale.set(command.destWidth, command.destHeight, 1);
    entry.mesh.visible = true;
  }
}
